import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import RelativeTime from 'dayjs/plugin/relativeTime';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(minMax);
dayjs.extend(utc);
dayjs.extend(LocalizedFormat);
dayjs.extend(RelativeTime);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(tz);

export class LocalDate {
  private readonly _dayjs: dayjs.Dayjs;

  constructor(val?: ConfigType) {
    this._dayjs = val instanceof LocalDate ? val.toDayjs() : dayjs(val).utc(true).startOf('d');
  }

  public format(template?: string): string {
    return this._dayjs.format(template ?? 'LL');
  }

  public toISOString(): string {
    return this._dayjs.format('YYYY-MM-DD');
  }

  public toString(): string {
    return this.toISOString();
  }

  public toJSON(): string {
    return this.toISOString();
  }

  public startOf(unit: dayjs.OpUnitType): LocalDate {
    return new LocalDate(this._dayjs.startOf(unit));
  }

  public day(): number {
    return this._dayjs.day();
  }

  public daysInMonth(): number {
    return this._dayjs.daysInMonth();
  }

  public endOf(unit: dayjs.OpUnitType): LocalDate {
    return new LocalDate(this._dayjs.endOf(unit));
  }

  public add(value: number, unit?: dayjs.ManipulateType): LocalDate {
    return new LocalDate(this._dayjs.add(value, unit));
  }

  public subtract(value: number, unit?: dayjs.ManipulateType): LocalDate {
    return new LocalDate(this._dayjs.subtract(value, unit));
  }

  public isSame(date?: ConfigType, unit?: dayjs.OpUnitType): boolean {
    return this._dayjs.isSame(new LocalDate(date).toDayjs(), unit);
  }

  public isSameOrBefore(date?: ConfigType, unit?: dayjs.OpUnitType): boolean {
    return this._dayjs.isSameOrBefore(new LocalDate(date).toDayjs(), unit);
  }

  public isSameOrAfter(date?: ConfigType, unit?: dayjs.OpUnitType): boolean {
    return this._dayjs.isSameOrAfter(new LocalDate(date).toDayjs(), unit);
  }

  public isBefore(date?: ConfigType, unit?: dayjs.OpUnitType): boolean {
    return this._dayjs.isBefore(new LocalDate(date).toDayjs(), unit);
  }

  public isAfter(date?: ConfigType, unit?: dayjs.OpUnitType): boolean {
    return this._dayjs.isAfter(new LocalDate(date).toDayjs(), unit);
  }

  public diff(
    date?: ConfigType,
    unit?: dayjs.QUnitType | dayjs.OpUnitType,
    float?: boolean
  ): number {
    return this._dayjs.diff(new LocalDate(date).toDayjs(), unit, float);
  }

  public toDayjs(): dayjs.Dayjs {
    return this._dayjs;
  }
}

type ConfigType = dayjs.ConfigType | LocalDate;
